/* eslint-disable react/jsx-boolean-value */

import React, { PureComponent, memo } from 'react';
import { Trans, useTranslation, withTranslation } from 'react-i18next';
import { reduxForm } from 'redux-form';
import PropTypes from 'prop-types';
import * as R from 'ramda';

import * as alert from '@common/services/alert';
import Modal from '@common/components/modal';
import { Button } from '@common/components/button';
import { Checkbox } from '@common/components/form/inputs/checkbox';
import Options from '@common/components/options';
import * as Form from '@common/components/form';
import createTeam from '../../actions/create-team';
import updateTeam from '../../actions/update-team';
import bulkUpdateUsers from '../../actions/bulk-update-team-users';
import validate from '../validators/team';

const {
  Row,
  Group,
  Label,
  TextInput,
  UsersInput,
} = Form;
const ETeamTypes = {
  SYNCED: 'synced',
  DEFAULT: 'default',
};

const ETeamFormSteps = {
  SELECT_TEAM_TYPE: 'select-team-type',
  SELECT_TEAM_FUNCTIONS: 'select-team-functions',
  DETAILS_FORM: 'save'
};

export function getTeamSyncLabelData(team) {
  if (!team || !Array.isArray(team.function_ids) || team.function_ids.length <= 0) {
    return null;
  }
  if (team.function) {
    return {
      functionName: team.function.name,
      functionsCount: team.function_ids.length
    };
  }
  return {
    functionName: null,
    functionsCount: team.function_ids.length
  };
}

export const TeamSyncLabel = memo(({ functionName, functionsCount }) => {
  const { t } = useTranslation();

  if (functionName) {
    if (functionsCount > 1) {
      return (
        <Trans
          i18nKey="network:forms_team_synchrnized_multiple_groups"
          values={{
            functionName,
            count: functionsCount - 1
          }}
          components={[<b />]}
        />
      );
    }
    return (
      <Trans
        i18nKey="network:forms_team_synchronized"
        values={{
          // teamName is actually the name of the function group,
          // the translation value is wrong
          teamName: functionName
        }}
        components={[<b />]}
      />
    );
  }
  return t('network:team_synchronized_function_group', {
    count: functionsCount
  });
});

class TeamForm extends PureComponent {
  static propTypes = {
    dispatch: PropTypes.func.isRequired,
    network: PropTypes.object.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    initialize: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
    team: PropTypes.object,
    functions: PropTypes.array,
    onClose: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.createContent = this.createContent.bind(this);
    this.handleSelectType = this.handleSelectType.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.onBackClick = this.onBackClick.bind(this);
    this.proceedToSave = this.proceedToSave.bind(this);

    const { team } = props;

    let step;
    let type;
    if (this.isUpdate()) {
      props.initialize({
        ...props.initialValues,
        ...R.pick(['id', 'name'], team),
      });

      if (this.isSyncedTeamUpdate()) {
        step = ETeamFormSteps.SELECT_TEAM_FUNCTIONS;
        type = ETeamTypes.SYNCED;
      } else {
        step = ETeamFormSteps.DETAILS_FORM;
        type = ETeamTypes.DEFAULT;
      }
    } else {
      step = ETeamFormSteps.SELECT_TEAM_TYPE;
      type = null;
    }

    this.state = {
      step,
      type,
      selectedFunctions: team?.function_ids || []
    };
  }

  isUpdate() {
    const { team } = this.props;
    return !!team;
  }

  isCreate() {
    return !this.isUpdate();
  }

  isSyncedTeamUpdate() {
    const { team } = this.props;
    return this.isUpdate() && team.function_ids?.length > 0;
  }

  handleSelectType(type) {
    const step = type === ETeamTypes.DEFAULT ?
      ETeamFormSteps.DETAILS_FORM : ETeamFormSteps.SELECT_TEAM_FUNCTIONS;
    this.setState({ type, step });
  }

  createHeader() {
    const { team, t } = this.props;
    return (
      <Modal.Title>
        {team ? t('network:forms_team_edit') : t('network:forms_team_create')}
      </Modal.Title>
    );
  }

  createContent() {
    const { type, step } = this.state;
    const { network, functions, team, t } = this.props;

    if (step === ETeamFormSteps.SELECT_TEAM_TYPE) {
      return (
        <Options
          onSelect={this.handleSelectType}
          items={[
            {
              icon: 'assignment_ind',
              value: ETeamTypes.SYNCED,
              title: t('network:forms_team_function_filter_title'),
              description: t('network:forms_team_function_filter_description'), // eslint-disable-line
            },
            {
              icon: 'group',
              value: ETeamTypes.DEFAULT,
              title: t('network:forms_team_function_manual_title'),
              description: t('network:forms_team_function_manual_description'), // eslint-disable-line
            },
          ]}
        />
      );
    }

    if (step === ETeamFormSteps.SELECT_TEAM_FUNCTIONS) {
      if (!functions.length) {
        return <small><Trans i18nKey="network:forms_team_function_empty" /></small>;
      }
      const { selectedFunctions } = this.state;
      return (
        <div className="selectTeamFunctions">
          <small>
            { t('network:select_function_groups_to_associate_with_team') }
          </small>
          <Options
            containerClassName="Functions"
            items={functions.map((item) => ({
              value: item.id,
              title: (
                <span>
                  <div className="functionName">{item.name}</div>
                  <Checkbox
                    size="large"
                    value={selectedFunctions.indexOf(item.id) > -1}
                    onChange={(checked) => {
                      const state = checked ?
                        { selectedFunctions: [...selectedFunctions, item.id] } :
                        {
                          selectedFunctions: selectedFunctions.filter((id) => {
                            return id !== item.id;
                          })
                        };
                      this.setState(state);
                    }}
                  />

                  <br />
                  <small>
                    <Trans
                      i18nKey="network:forms_team_function_member_count"
                      values={{ count: item.users_count }}
                    />
                  </small>

                </span>
              )
            }))}
          />
        </div>
      );
    }

    return (
      <div>
        <Row>
          <Group>
            <Label for="name" text={t('network:forms_team_name')} />
            <TextInput name="name" placeholder={t('network:forms_team_name_placeholder')} />
          </Group>
        </Row>
        {
          type === ETeamTypes.DEFAULT && (
            <>
              <hr />
              <Row>
                <Group>
                  <Label text={t('network:forms_team_employees')} />
                  <UsersInput
                    name="members"
                    isUserSelected={team ? (user) => R.contains(user.id, team.member_ids) : R.F}
                    network={network}
                  />
                </Group>
              </Row>
            </>
          )
        }
      </div>
    );
  }

  isNextDisabled() {
    const { selectedFunctions } = this.state;
    return selectedFunctions.length <= 0;
  }

  proceedToSave() {
    this.setState({ step: ETeamFormSteps.DETAILS_FORM });
  }

  onBackClick() {
    const { step, type } = this.state;
    if (step === ETeamFormSteps.DETAILS_FORM && type === ETeamTypes.SYNCED) {
      this.setState({ step: ETeamFormSteps.SELECT_TEAM_FUNCTIONS });
    } else { // surely we are in ETeamFormSteps.SELECT_TEAM_FUNCTIONS
      this.setState({
        type: null,
        step: ETeamFormSteps.SELECT_TEAM_TYPE
      });
    }
  }

  createFooter() {
    const { type, step } = this.state;
    const { team, submitting, invalid, t } = this.props;

    return (
      <>
        {
          (
            this.isCreate() || (
              step === ETeamFormSteps.DETAILS_FORM && type === ETeamTypes.SYNCED
            )
          ) && (
            <Button onClick={this.onBackClick}>
              <Trans i18nKey="network:forms_team_back" />
            </Button>
          )
        }
        {
          step === ETeamFormSteps.DETAILS_FORM && (
            <Button
              type="primary"
              isLoading={submitting}
              disabled={invalid}
              buttonType="submit"
            >
              {!team ? t('network:forms_team_submit') : t('network:forms_team_submit_edit')}
            </Button>
          )
        }
        {
          step === ETeamFormSteps.SELECT_TEAM_FUNCTIONS && (
            <Button
              type="primary"
              disabled={this.isNextDisabled()}
              onClick={this.proceedToSave}
            >
              { t('common:carousel_footer_next') }
            </Button>
          )
        }
      </>
    );
  }

  handleOpen() {
    this.setState({ type: null });
  }

  handleClose() {
    this.props.reset();
    this.setState({ // reset unsaved state
      selectedFunctions: [],
      step: ETeamFormSteps.SELECT_TEAM_TYPE,
      type: null
    });
    if (this.props.onClose) this.props.onClose();
  }

  async handleSubmit(values, dispatch) {
    const { t } = this.props;
    const { type, selectedFunctions } = this.state;

    let team;
    try {
      const functionIds = type === ETeamTypes.SYNCED ? selectedFunctions : undefined;
      const formattedValues = {
        ...values,
        functionIds
      };
      if (values.id) {
        ({ team } = await dispatch(updateTeam(formattedValues)));
      } else {
        ({ team } = await dispatch(createTeam(formattedValues)));
      }

      if (values.members) {
        await dispatch(bulkUpdateUsers(team.id, values.members, team));
      }

      if (values.id) {
        alert.success(t('network:forms_team_edit_success'));
      } else {
        alert.success(t('network:forms_team_create_success', {
          groupName: values.name
        }));
      }
      this.handleClose();
    } catch (error) {
      alert.forStatus(error.status_code, {
        warning: values.id
          ? t('network:forms_team_edit_warning')
          : t('network:forms_team_create_warning'),
        error: values.id
          ? t('network:forms_team_edit_error')
          : t('network:forms_team_create_error'),
      });
      throw error;
    }
  }

  render() {
    const { step } = this.state;
    const { team, handleSubmit } = this.props;

    return (
      <Modal
        list
        size="small"
        className="Form TeamForm"
        onEnter={!team ? this.handleOpen : undefined}
        onExited={this.handleClose}
        header={this.createHeader()}
        content={this.createContent()}
        footer={
          step === ETeamFormSteps.SELECT_TEAM_TYPE ? null : this.createFooter()
        }
        wrapper={Modal.FormWrapper}
        wrapperProps={{
          onSubmit: handleSubmit(this.handleSubmit),
        }}
      />
    );
  }
}

export default withTranslation()(reduxForm({
  initialValues: { name: '', members: { add: [], remove: [] } },
  validate,
})(TeamForm));
